import { ref, shallowRef } from 'vue';
import {
  init as _init,
  setUserId as _setUserId,
  reset,
  track,
  flush as _flush,
  getDeviceId as _getDeviceId,
  add,
  identify,
  Identify,
} from '@amplitude/analytics-browser';
import {
  Experiment,
  type ExperimentClient,
  type ExperimentConfig,
} from '@amplitude/experiment-js-client';

import { debug } from '../debug';

export const setUserId = (userId: string) => {
  debug('Amplitude', 'setUserId', userId);
  _setUserId(userId);
};

/** Resets user and device ids */
export const invalidateUser = () => {
  debug('Amplitude', 'invalidateUser');
  reset();
};

export const logEvent = (
  event: string,
  properties?: Record<string, unknown>,
) => {
  debug(
    { prefix: 'Amplitude', shouldLogInStageEnv: true },
    'logEvent',
    event,
    properties,
  );
  track(event, properties);
};

/**
 * Sets user properties that will be attached to all subsequent events for the
 * current user. Use this for stable, cross-event attributes (e.g. A/B test
 * variant) so you don't have to thread them through every event call site.
 */
export const setUserProperties = (properties: Record<string, unknown>) => {
  debug(
    { prefix: 'Amplitude', shouldLogInStageEnv: true },
    'setUserProperties',
    properties,
  );
  const identifyEvent = new Identify();
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined) {
      continue;
    }
    identifyEvent.set(key, value as never);
  }
  identify(identifyEvent);
};

/**
 * Forces queued events to be sent immediately and resolves once the flush
 * completes. Use before navigating away (e.g. `window.location.href = ...`)
 * to ensure pending events reach Amplitude before the page is torn down.
 * Never rejects — navigation should not be blocked by analytics failures.
 */
export const flushEvents = () => {
  return _flush().promise.catch(err => {
    debug('Amplitude', 'flushEvents failed', err);
  });
};

export const getDeviceId = () => {
  /**
   * There is no amplitude instance in TEST mode while running vitest
   * That's why we check for instance here
   * Otherwise we would want to mock amplitude instance whenever pfm is used in tests
   */
  return _getDeviceId() ?? null;
};

export const addPlugin = (...args: Parameters<typeof add>) => {
  return add(...args);
};

export const init = (amplitudeApiKey: string) => {
  return _init(amplitudeApiKey, { defaultTracking: false }).promise.then(
    () => debug('Amplitude', 'Client initialized'),
    err => {
      debug('Amplitude', 'Failed to init client');
      throw err;
    },
  );
};

/**
 * @description Keep the Experiment client in a shallow ref so Vue does not
 * wrap the SDK internals, preventing recursive updates when the client mutates
 * itself
 */
const experiment = shallowRef<ExperimentClient | null>(null);
const isExperimentLoading = ref(true);

export function useAmplitudeExperiment() {
  const initExperiment = (
    amplitudeApiKey: string,
    config?: ExperimentConfig,
  ) => {
    const client = Experiment.initializeWithAmplitudeAnalytics(
      amplitudeApiKey,
      config,
    );
    experiment.value = client;
    client.start().finally(() => {
      isExperimentLoading.value = false;
    });
    debug('Amplitude', 'Experiment initialized');
  };

  return { experiment, initExperiment, isExperimentLoading };
}
